import React, { useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import SearchRounded from '@mui/icons-material/SearchRounded';

// New UI for User groups (docs/ui_redesign/14-screen-concepts.md, "Users and permissions", option A): the groups in a
// list on the left; the chosen group's roles and switches on top; its rights as one row per right with Media,
// Production and Broadcast side by side; Reset, Delete and Save in a bar under the rights. The page's own state,
// controls, dialogs and handlers (UserRoles_New.js) are passed in, so everything saves exactly as in Classic.

// "Media_Invoice Correction" -> ['Media', 'Invoice Correction'] (UserRoles_New builds the ids this way)
export const splitRightId = id => { const i = String(id).indexOf('_'); return i < 0 ? ['', String(id)] : [id.slice(0, i), id.slice(i + 1)]; };

export function rightsMatrix(permissions, search = '') {
	const modules = []; const rights = [];
	Object.keys(permissions || {}).forEach(id => {
		const [m, r] = splitRightId(id);
		if (!modules.includes(m)) modules.push(m);
		if (!rights.includes(r)) rights.push(r);
	});
	const q = search.trim().toLowerCase();
	return { modules, rights: q ? rights.filter(r => r.toLowerCase().includes(q)) : rights };
}

export const isYes = v => v === true || v === 'Y';

// The groups as a list to pick from (User groups and Group rules). The page's own "select a group" handler runs.
export function GroupList({ groups = [], value, onSelect, isAdmin = () => false }) {
	return (
		<nav className="apw2-ug-groups" aria-label="User groups">
			<div className="apw2-ug-groups-h">Groups <span>{groups.length}</span></div>
			<ul>
				{groups.map(g => (
					<li key={g}>
						<button type="button" className={g === value ? 'on' : ''} aria-current={g === value ? 'true' : undefined} onClick={() => onSelect(g)}>
							<span className="nm">{g}</span>
							{isAdmin(g) ? <span className="apw2-pill">Admin</span> : null}
						</button>
					</li>
				))}
			</ul>
		</nav>
	);
}

export default function UserGroupsLayout({
	groups = [], selectedGroup, onSelectGroup, userRoleMap = {}, permissions = {}, onPermissionChange, dirty, searchText = '', onSearch,
	addGroupButton, rolesSelect, roleChips, adminCheckbox, routingCheckbox, approvalRoutingCheckbox, resetButton, deleteButton, saveButton, dialogs
}) {
	const [search, setSearch] = useState(searchText);
	const { modules, rights } = rightsMatrix(permissions, search);
	const anyEmail = m => rights.some(r => permissions[m + '_' + r] && permissions[m + '_' + r].hasEmail);
	return (
		<div className="apw2-ug">
			<div className="apw2-ug-head">
				<h1>User group management</h1>
				<span className="apw2-grow" />
				{addGroupButton}
			</div>
			<div className="apw2-ug-layout">
				<GroupList groups={groups} value={selectedGroup} onSelect={onSelectGroup} isAdmin={g => userRoleMap[g] && isYes(userRoleMap[g].isAdmin)} />
				<div className="apw2-ug-main">
					<section className="apw2-ug-card apw2-ug-settings" aria-label="Group settings">
						<div className="apw2-ug-card-h">
							<h2>{selectedGroup || 'Group settings'}</h2>
							<span className="apw2-grow" />
							{deleteButton ? React.cloneElement(deleteButton, { className: 'apw2-btn sm danger-outline', style: undefined }, 'Delete group…') : null}
						</div>
						<div className="apw2-ug-field">
							<span className="apw2-ug-l">Assigned role(s)</span>
							<div className="apw2-ug-roles">{rolesSelect}</div>
							{roleChips && roleChips.length ? <div className="apw2-ug-chips">{roleChips}</div> : null}
						</div>
						<label className="apw2-ug-toggle">{adminCheckbox}<span>Admin</span></label>
						<label className="apw2-ug-toggle">{routingCheckbox}<span>Allow routing setup</span></label>
						<label className="apw2-ug-toggle">{approvalRoutingCheckbox}<span>Allow approval routing setup</span></label>
					</section>
					<section className="apw2-ug-card apw2-ug-rights" aria-label="Rights">
						<div className="apw2-ug-rights-h">
							<h2>Rights</h2>
							<span className="apw2-grow" />
							<label className="apw2-ug-search"><SearchRounded fontSize="small" aria-hidden="true" />
								<input type="search" placeholder="Find a right" aria-label="Find a right" value={search} onChange={e => { setSearch(e.target.value); if (onSearch) onSearch(e.target.value); }} />
							</label>
						</div>
						<div className="apw2-ug-table-wrap">
							<table className="apw2-ug-table">
								<thead>
									<tr>
										<th rowSpan={2} scope="col" className="r">Right</th>
										{modules.map(m => <th key={m} colSpan={anyEmail(m) ? 2 : 1} scope="colgroup" className="m">{m}</th>)}
									</tr>
									<tr>
										{modules.map(m => (
											<React.Fragment key={m}>
												<th scope="col" className="m first">Enable</th>
												{anyEmail(m) ? <th scope="col">Email</th> : null}
											</React.Fragment>
										))}
									</tr>
								</thead>
								<tbody>
									{rights.map(r => (
										<tr key={r}>
											<th scope="row" className="r">{r}</th>
											{modules.map(m => {
												const id = m + '_' + r;
												const p = permissions[id];
												const email = anyEmail(m);
												if (!p) return <td key={m} className="m first na" colSpan={email ? 2 : 1} title={`${m} doesn't use this right`}>—</td>;
												return (
													<React.Fragment key={m}>
														<td className="m first">
															<Checkbox size="small" checked={!!p.enabled} onChange={e => onPermissionChange(id, 'enabled', e.target.checked)} inputProps={{ 'aria-label': `${r}, ${m}: enable` }} />
														</td>
														{email ? (
															<td>{p.hasEmail ? <Checkbox size="small" checked={!!p.email} disabled={!p.enabled} onChange={e => onPermissionChange(id, 'email', e.target.checked)} inputProps={{ 'aria-label': `${r}, ${m}: email` }} /> : null}</td>
														) : null}
													</React.Fragment>
												);
											})}
										</tr>
									))}
									{!rights.length && <tr><td className="apw2-ug-none" colSpan={1 + modules.length * 2}>{search ? 'No right matches the search.' : 'Choose a group to see its rights.'}</td></tr>}
								</tbody>
							</table>
						</div>
						<div className="apw2-savebar" role="region" aria-label="Save changes">
							<span className="apw2-savebar-note">{dirty ? <b>Unsaved changes</b> : 'No changes'}</span>
							<span className="apw2-grow" />
							{resetButton}
							{saveButton}
						</div>
					</section>
				</div>
			</div>
			{dialogs}
		</div>
	);
}
